import { gate } from 'hub-http/gates';
import FormattedMessage from 'I18n/components/FormattedMessage';
import formatName from 'I18n/utils/formatName';
import { TFlex } from 'foundations-components/transitional/components/TFlex';
import { Text } from 'foundations-components/components/Text';
import { captureNetworkError } from 'growth-onboarding-reliability/utils/raven';
import { useAllAssignedTasks, useEligibleUsersForTask, useHasEligibleUsers } from 'setup-guide-api/dataFetchingClient/queries/assignedTasks';
import { AssignTaskLink } from './AssignTaskLink';
import { useAuth } from '../../../../hooks/auth';
import { useTaskAssignmentAudience } from '../../../../hooks/useTaskAssignmentAudience';
import { jsx as _jsx } from "react/jsx-runtime";
import { jsxs as _jsxs } from "react/jsx-runtime";
const useHasEligibleUsersByGate = ({
  taskId,
  isTaskAssignable
}) => {
  const {
    user,
    gates = []
  } = useAuth();
  const {
    user_id: userId
  } = user;
  const shouldUseNewEligibleUsersEndpoint = gates.includes(gate('GrowthAdoptionCore:AssignedTasksEligibleUsers'));
  // New hasEligibleUsers endpoint
  const {
    hasEligibleUsers
  } = useHasEligibleUsers({
    variables: {
      taskId
    },
    skip: !isTaskAssignable || !shouldUseNewEligibleUsersEndpoint
  });
  // Old getEligibleUsers endpoint
  const {
    eligibleUsers = []
  } = useEligibleUsersForTask({
    variables: {
      taskId
    },
    skip: !isTaskAssignable || !userId || shouldUseNewEligibleUsersEndpoint
  });
  const hasEligibleUsersLegacy = eligibleUsers === null || eligibleUsers === void 0 ? void 0 : eligibleUsers.some(eligibleUser => eligibleUser.userId !== userId);
  return shouldUseNewEligibleUsersEndpoint ? hasEligibleUsers : hasEligibleUsersLegacy;
};
export const TaskAssignmentLinks = ({
  task,
  hideIfNoAssignment = false
}) => {
  const isInAudience = useTaskAssignmentAudience();
  const isTaskAssignable = isInAudience && task.associationType === 'PORTAL';
  const {
    user_id: userId
  } = useAuth().user;
  const hasEligibleUsers = useHasEligibleUsersByGate({
    taskId: task.id,
    isTaskAssignable
  });
  const {
    allAssignedTasks = [],
    loading,
    refetch
  } = useAllAssignedTasks();
  const assignedTask = allAssignedTasks.find(currentTask => currentTask.taskId === task.id);
  if (!isTaskAssignable || hideIfNoAssignment && !assignedTask || loading) {
    return null;
  }
  const handleClose = () => {
    refetch().catch(captureNetworkError);
  };
  const assignee = formatName({
    firstName: assignedTask === null || assignedTask === void 0 ? void 0 : assignedTask.firstName,
    lastName: assignedTask === null || assignedTask === void 0 ? void 0 : assignedTask.lastName
  });
  if (!assignedTask) {
    if (!hasEligibleUsers) {
      return null;
    }
    return /*#__PURE__*/_jsx(AssignTaskLink, {
      onClose: handleClose,
      task: task,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "setup-guide-components.assignTaskButton.label"
      })
    });
  }
  if (assignedTask.assigneeCompletedTask) {
    return /*#__PURE__*/_jsx(Text, {
      as: "h7",
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "setup-guide-components.assignTaskButton.completedBy",
        options: {
          name: assignee
        }
      })
    });
  }
  if (assignedTask.assigneeSkippedTask) {
    return /*#__PURE__*/_jsxs(TFlex, {
      direction: "column",
      children: [/*#__PURE__*/_jsx(Text, {
        as: "h7",
        marginBlockEnd: "0",
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "setup-guide-components.assignTaskButton.skippedBy",
          options: {
            name: assignee
          }
        })
      }), /*#__PURE__*/_jsx(AssignTaskLink, {
        assignedTaskId: assignedTask.id,
        onClose: handleClose,
        task: task,
        children: /*#__PURE__*/_jsx(FormattedMessage, {
          message: "setup-guide-components.assignTaskButton.label"
        })
      })]
    });
  }
  const isAssignedToCurrentUser = (assignedTask === null || assignedTask === void 0 ? void 0 : assignedTask.userId) === userId;
  if (isAssignedToCurrentUser) {
    return /*#__PURE__*/_jsx(AssignTaskLink, {
      assignedTaskId: assignedTask.id,
      onClose: handleClose,
      task: task,
      children: /*#__PURE__*/_jsx(FormattedMessage, {
        message: "setup-guide-components.assignTaskButton.assignedToCurrentUser"
      })
    });
  }
  return /*#__PURE__*/_jsx(AssignTaskLink, {
    assignedTaskId: assignedTask.id,
    onClose: handleClose,
    task: task,
    children: assignee ? /*#__PURE__*/_jsx(FormattedMessage, {
      message: "setup-guide-components.assignTaskButton.assignedTo",
      options: {
        name: assignee
      }
    }) : /*#__PURE__*/_jsx(FormattedMessage, {
      message: "setup-guide-components.assignTaskButton.assignedToNewUser"
    })
  });
};